import {createAction, createAsyncThunk} from '@reduxjs/toolkit';

import getEventsDayOverview from 'api/events/get-events-day-overview';

import {EventListItem} from 'api/events/events.types';
import dayjs from 'utils/dayjs';
import {DayOverviewState} from 'pages/event-management/store/day-overview-slice';

interface GetDayOverviewDataParams {
    facilityId: number;
    date: string;
}

interface GetDayOverviewDataReturn {
    counts: {
        blackouts: number;
        dailyEvents: number;
        monthly: number;
        transientAirport: number;
    };
    events: EventListItem[];
}

interface GetDayOverviewDataThunkAPI {
    state: {
        events: {
            dayOverview: DayOverviewState;
        };
    };
    rejectValue: unknown;
}

/**
 * Formating function to sort events by their start date in ascending order.
 */
const sortEventsByStartDate = (events: EventListItem[]): EventListItem[] => {
    const sortedEvents = [...events].sort((a, b) => {
        const startDateA = dayjs(a.eventStarts);
        const startDateB = dayjs(b.eventStarts);

        return startDateA.valueOf() - startDateB.valueOf();
    });

    return sortedEvents;
};

/**
 * A Redux thunk to get the events day overview data.
 *
 * Action Type: events/day-overview/getEventsDayOverviewData
 *
 * API Service: {@link getEventsDayOverview}
 */
export const getEventsDayOverviewData = createAsyncThunk<
    // Return type of the payload creator
    GetDayOverviewDataReturn,
    // First argument to the payload creator
    GetDayOverviewDataParams,
    // ThunkAPI type
    GetDayOverviewDataThunkAPI
>(
    'events/day-overview/getEventsDayOverviewData',
    async ({facilityId, date}, {rejectWithValue}) => {
        try {
            const formatedDate = dayjs(date).format('YYYY-MM-DD');

            const response = await getEventsDayOverview(
                facilityId,
                formatedDate
            );

            const sortedEvents = sortEventsByStartDate(response.data.events);

            const formattedResponse = {
                counts: response.data.counts,
                events: sortedEvents,
            };

            return formattedResponse;
        } catch (error) {
            console.error('getEventsDayOverviewData error:', {error});

            return rejectWithValue(error);
        }
    }
);

/**
 * An action to update the selected date for the day overview page.
 */
export const setSelectedDate = createAction(
    'events/day-overview/setSelectedDate',
    (date: string) => {
        return {
            payload: date,
        };
    }
);
